import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import { useLanguage } from '../context/LanguageContext';
import { HeroSlide } from '../types';
import { ChevronLeft, ChevronRight, Sparkles, Tag, Gift, Flame, Utensils } from 'lucide-react';

interface HeroSliderProps {
  onApplyCoupon?: (code: string) => void;
}

export const HeroSlider: React.FC<HeroSliderProps> = ({ onApplyCoupon }) => {
  const { heroSlides, heroSettings, applyCoupon } = useApp();
  const { lang, t } = useLanguage();

  // Active slides only, sorted by sort_order
  const activeSlides = useMemo(() => {
    const valid = (heroSlides || []).filter(s => s.is_active);
    return valid.sort((a, b) => a.sort_order - b.sort_order);
  }, [heroSlides]);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [appliedToast, setAppliedToast] = useState<string | null>(null);

  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);

  // If activeSlides is empty, fallback to legacy heroSettings
  const slideCount = activeSlides.length;

  // Auto-play interval
  useEffect(() => {
    if (slideCount <= 1 || isPaused) return;

    const timer = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % slideCount);
    }, 5500);

    return () => clearInterval(timer);
  }, [slideCount, isPaused]);

  // Adjust index if active slides count shrinks
  useEffect(() => {
    if (currentIndex >= slideCount && slideCount > 0) {
      setCurrentIndex(0);
    }
  }, [slideCount, currentIndex]);

  const handlePrev = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (slideCount <= 1) return;
    setCurrentIndex(prev => (prev === 0 ? slideCount - 1 : prev - 1));
  };

  const handleNext = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (slideCount <= 1) return;
    setCurrentIndex(prev => (prev + 1) % slideCount);
  };

  // Touch swipe handling for mobile devices
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    setIsPaused(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (touchStartX.current !== null && touchEndX.current !== null) {
      const diffX = touchStartX.current - touchEndX.current;
      const swipeThreshold = 45; // min px for swipe

      if (diffX > swipeThreshold) {
        handleNext();
      } else if (diffX < -swipeThreshold) {
        handlePrev();
      }
    }
    touchStartX.current = null;
    touchEndX.current = null;
    setIsPaused(false);
  };

  const handleSlideClick = (slide: HeroSlide) => {
    if (slide.discount_code) {
      const res = applyCoupon(slide.discount_code);
      if (res.success) {
        setAppliedToast(`✓ Voucher "${slide.discount_code}" applied to your order!`);
      } else {
        setAppliedToast(res.message);
      }
      setTimeout(() => {
        setAppliedToast(null);
      }, 3500);

      if (onApplyCoupon) {
        onApplyCoupon(slide.discount_code);
      }
    }
  };

  // Fallback if no slides exist
  if (slideCount === 0) {
    const heroTitle = lang === 'en' ? heroSettings.title_en : lang === 'sv' ? heroSettings.title_sv : heroSettings.title_fi;
    const heroDesc = lang === 'en' ? heroSettings.desc_en : lang === 'sv' ? heroSettings.desc_sv : heroSettings.desc_fi;

    return (
      <div
        className="hero-header"
        style={{
          backgroundImage: heroSettings.cover_image_url
            ? `linear-gradient(to bottom, rgba(15, 17, 21, 0.4), rgba(15, 17, 21, 0.88)), url(${heroSettings.cover_image_url})`
            : undefined
        }}
      >
        <div className="hero-tags">
          {(heroSettings.tags || []).map((tag, idx) => {
            const tagText = lang === 'en' ? tag.text_en : lang === 'sv' ? tag.text_sv : tag.text_fi;
            return (
              <span key={idx} className="hero-tag">
                <span className="hero-tag-icon">{tag.icon}</span>
                <span>{tagText}</span>
              </span>
            );
          })}
        </div>
        <h1 className="hero-title">{heroTitle}</h1>
        <p className="hero-desc">{heroDesc}</p>
      </div>
    );
  }

  const currentSlide = activeSlides[currentIndex] || activeSlides[0];
  const slideTitle = lang === 'en' ? currentSlide.title_en : lang === 'sv' ? currentSlide.title_sv : currentSlide.title_fi;
  const slideSubtitle = lang === 'en' ? currentSlide.subtitle_en : lang === 'sv' ? currentSlide.subtitle_sv : currentSlide.subtitle_fi;
  const ctaLabel = lang === 'en' ? currentSlide.cta_label_en : lang === 'sv' ? currentSlide.cta_label_sv : currentSlide.cta_label_fi;

  const bgImage = currentSlide.image_url || heroSettings.cover_image_url || 'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?auto=format&fit=crop&w=1400&q=80';
  const customGradient = currentSlide.bg_gradient || 'linear-gradient(180deg, rgba(15, 17, 21, 0.35) 0%, rgba(15, 17, 21, 0.88) 100%)';

  return (
    <div
      className="hero-carousel-wrapper"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Toast feedback when clicking a coupon slide */}
      {appliedToast && (
        <div className="hero-slide-toast">
          <Sparkles size={16} color="var(--accent-copper)" />
          <span>{appliedToast}</span>
        </div>
      )}

      {/* Main Active Slide Display */}
      <div
        className="hero-slide-card"
        onClick={() => handleSlideClick(currentSlide)}
        style={{
          backgroundImage: `${customGradient}, url(${bgImage})`,
          cursor: currentSlide.discount_code ? 'pointer' : 'default'
        }}
      >
        {/* Top Badges / Tags */}
        <div className="hero-slide-top-row">
          {currentSlide.badge_text && (
            <span className="hero-slide-badge">
              {currentSlide.slide_type === 'promo_offer' && <Gift size={13} />}
              {currentSlide.slide_type === 'brand_hero' && <Flame size={13} />}
              {currentSlide.slide_type === 'dish_highlight' && <Utensils size={13} />}
              <span>{currentSlide.badge_text}</span>
            </span>
          )}

          {/* If slide has tags list */}
          {currentSlide.tags && currentSlide.tags.length > 0 && (
            <div className="hero-tags">
              {currentSlide.tags.map((tag, idx) => {
                const tagText = lang === 'en' ? tag.text_en : lang === 'sv' ? tag.text_sv : tag.text_fi;
                return (
                  <span key={idx} className="hero-tag">
                    <span className="hero-tag-icon">{tag.icon}</span>
                    <span>{tagText}</span>
                  </span>
                );
              })}
            </div>
          )}

          {/* Coupon Code Pill if available */}
          {currentSlide.discount_code && (
            <div className="hero-slide-coupon-pill" title="Tap to apply discount">
              <Tag size={12} />
              <span>{currentSlide.discount_code}</span>
            </div>
          )}
        </div>

        {/* Slide Content: Title & Subtitle */}
        <div className="hero-slide-body">
          <h1 className="hero-slide-title">{slideTitle}</h1>
          {slideSubtitle && <p className="hero-slide-desc">{slideSubtitle}</p>}
        </div>

        {/* Slide Action Bar (If coupon or CTA present) */}
        {currentSlide.discount_code && (
          <div className="hero-slide-action-row">
            <button type="button" className="hero-slide-cta-btn">
              <Sparkles size={14} />
              <span>{ctaLabel || `Tap to Apply ${currentSlide.discount_code}`}</span>
            </button>
          </div>
        )}
      </div>

      {/* Desktop Prev / Next Navigation Arrows */}
      {slideCount > 1 && (
        <>
          <button
            type="button"
            className="hero-slider-nav-btn prev"
            onClick={handlePrev}
            aria-label="Previous Slide"
          >
            <ChevronLeft size={20} />
          </button>

          <button
            type="button"
            className="hero-slider-nav-btn next"
            onClick={handleNext}
            aria-label="Next Slide"
          >
            <ChevronRight size={20} />
          </button>
        </>
      )}

      {/* Pagination Dots Indicator */}
      {slideCount > 1 && (
        <div className="hero-slider-dots">
          {activeSlides.map((slide, idx) => (
            <button
              key={slide.id || idx}
              type="button"
              className={`hero-slider-dot ${idx === currentIndex ? 'active' : ''}`}
              onClick={e => {
                e.stopPropagation();
                setCurrentIndex(idx);
              }}
              aria-label={`Go to slide ${idx + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};
